import { createContext, useCallback, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react';

export const ToastContext = createContext(null);

const TYPE_STYLES = {
  success: 'bg-success-bg text-success border-success/20',
  error: 'bg-danger-bg text-danger border-danger/20',
  info: 'bg-cream-100 text-bordeaux-700 border-bordeaux-600/20 dark:bg-zinc-900 dark:text-bordeaux-300 dark:border-bordeaux-400/30',
};
const TYPE_ICONS = {
  success: CheckCircle2,
  error: AlertTriangle,
  info: Info,
};

/**
 * App-wide toast surface. Migrated teal → bordeaux (mai 2026).
 */
export default function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const counter = useRef(0);
  const reduce = useReducedMotion();

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    ({ message, type = 'info', duration = 3000 }) => {
      const id = ++counter.current;
      setToasts((list) => [...list, { id, message, type }]);
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss],
  );

  const api = useMemo(
    () => ({
      push,
      success: (message, opts) => push({ message, type: 'success', ...opts }),
      error: (message, opts) => push({ message, type: 'error', ...opts }),
      info: (message, opts) => push({ message, type: 'info', ...opts }),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-end gap-2 px-4 pb-4 sm:px-6 sm:pb-6"
      >
        <AnimatePresence>
          {toasts.map(({ id, message, type }) => {
            const Icon = TYPE_ICONS[type] ?? Info;
            return (
              <motion.div
                key={id}
                role={type === 'error' ? 'alert' : 'status'}
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, y: 24 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                className={`pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-card border px-4 py-3 shadow-card-hover ${TYPE_STYLES[type]}`}
              >
                <Icon size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
                <p className="flex-1 text-sm leading-relaxed">{message}</p>
                <button
                  type="button"
                  onClick={() => dismiss(id)}
                  aria-label="Close notification"
                  className="rounded-btn p-1 transition-colors hover:bg-black/5"
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}
